
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteAdBanner, getAdsList, viewAdBanner } from "../../Redux/Actions/banner_adsActions";
import TableComponentCp from "../../Global Functions/tableComponentsCp";
import { ToFirstCapital } from "../../Utils/helpers";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Button, Dropdown, Menu, Modal, message } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";

const ListAds = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  // Modal states
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  // Selected record state
  const [selectedRecord, setSelectedRecord] = useState(null);

  // For edit form
  // const [form] = Form.useForm();

  const fetchDetails = useCallback(
    (page, limit, filter) => {
      const adsData = {
        page,
        limit,
        is_deleted: false,
        filter: filter ?? debouncedSearch,
        ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
        ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
      };
      dispatch(getAdsList(adsData));
    },
    [debouncedSearch, dateRange.start, dateRange.end, dispatch]
  );

  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, fetchDetails]);

  const { adsBanners, loading } = useSelector((state) => state.adsBanners);

  useEffect(() => {
    if (adsBanners?.total) {
      setPagination((prevData) => ({ ...prevData, total: adsBanners.total }));
    }
  }, [adsBanners]);

  const filterHandler = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleDateRangeApply = (start, end) => {
    if (start === null && end === null) {
      setDateRange({ start: null, end: null });
    } else {
      setDateRange({ start, end });
    }
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };


  const onViewClick = async (record) => {
    try {
      const response = await dispatch(viewAdBanner(record._id));
      if (response?.statusCode === 200) {
        setSelectedRecord(response.data);
        setViewModalVisible(true);
      } else {
        message.error("Failed to fetch ad details");
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const onDeleteClick = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const onEditClick = (record) => {
    navigate(`/ads,_banner/edit/${record._id}`);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await dispatch(deleteAdBanner(selectedRecord._id));
      if (response?.statusCode === 200) {
        setDeleteModalVisible(false);
        fetchDetails(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error("Delete error:", error);
    }
  };
  const columnsAds = [
    {
      title: "Sr.No.",
      align: "center",
      key: "srno",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Title",
      align: "center",
      dataIndex: "title",
      key: "title",
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 200, // adjust as per UI space
              cursor: "pointer",
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 200, // adjust as per UI space
              cursor: "pointer",
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Banner",
      align: "center",
      dataIndex: "cover_photo",
      key: "cover_photo",
      render: (_, record, index) => (
        <img
          key={index}
          style={{ height: "50px", width: "50px", borderRadius: "40px" }}
          alt=""
          src={record?.cover_photo || ""}
        />
      ),
    },
    {
      title: "Type",
      align: "center",
      dataIndex: "type",
      key: "type",
      render: (record) => ToFirstCapital(record),
    },
    {
      title: "Action",
      align: "center",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="view" onClick={() => onViewClick(record)}>
                View
              </Menu.Item>
              <Menu.Item key="edit" onClick={() => onEditClick(record)}>
                Edit
              </Menu.Item>
              <Menu.Item key="delete" onClick={() => onDeleteClick(record)}>
                Delete
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button shape="square" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const columns = useMemo(() => columnsAds, []);

  const dataSource = useMemo(
    () => (Array.isArray(adsBanners?.data) ? adsBanners.data : []),
    [adsBanners?.data]
  );

  return (
    <>
      <TableComponentCp
        h3heading="Ads,Banners List"
        setState={setSearchQuery}
        filterFunction={filterHandler}
        buttonhead2="Add Ad or Banner"
        navigateTo2="/ads,_banner/add"
        onCalendarClick={() => setIsDatePopupVisible(true)}
        pagination={pagination}
        loading={loading}
        columns={columns}
        dataSource={dataSource}
      />

      {/* View Modal */}
      <Modal
        visible={viewModalVisible}
        title="View Ad/Banner"
        footer={null}
        onCancel={() => setViewModalVisible(false)}
      >
        {selectedRecord ? (
          <div>
            <p><strong>Title:</strong> {selectedRecord.title}</p>
            <p><strong>Description:</strong> {selectedRecord.description}</p>
            <p><strong>Type:</strong> {ToFirstCapital(selectedRecord.type)}</p>
            <p><strong>Status:</strong> {ToFirstCapital(selectedRecord.status)}</p>
            <p><strong>Start Date:</strong> {new Date(selectedRecord.start_date).toLocaleDateString()}</p>
            <p><strong>End Date:</strong> {new Date(selectedRecord.end_date).toLocaleDateString()}</p>
            {selectedRecord.external_link && <p><strong>External Link:</strong> <a href={selectedRecord.external_link} target="_blank" rel="noopener noreferrer">{selectedRecord.external_link}</a></p>
            }

            {selectedRecord.cover_photo && (
              <div style={{ marginTop: 10 }}>
                <strong>Banner:</strong><br />
                <img
                  alt="Banner"
                  src={selectedRecord.cover_photo}
                  style={{ width: "100%", maxHeight: "300px", objectFit: "contain", marginTop: 5 }}
                />
              </div>
            )}
          </div>
        ) : (
          <p>No data</p>
        )}
      </Modal>
      <Modal
        visible={deleteModalVisible}
        title="Confirm Delete"
        okText="Delete"
        okType="danger"
        onCancel={() => setDeleteModalVisible(false)}
        onOk={handleDeleteConfirm}
      >
        <p>Are you sure you want to delete this ad/banner?</p>
      </Modal>

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </>
  );
};

export default ListAds;
